import { Icon } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { getISOWeek, getYear } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { useGetTemplates } from "../../../../api/useCalendarTemplateQueries";
import { CalendarTemplate } from "../../../../api/types";
import { useUserHasRole } from "../../../../hooks/userHooks";
import { TemplateItemBg, TemplateItem } from "./selectTemplateButton.styles";
import { ConfirmApplyCalendarTemplateModal } from "../../CalendarTemplates/ConfirmApplyCalendarTemplateModal";
import { useWeekDate } from "../../hooks/useWeekDate";

export const CalendarTemplates = () => {
  const { t } = useTranslation();
  const isPsychologist = useUserHasRole("Psychologist");
  const { data, isLoading } = useGetTemplates({ enabled: isPsychologist });
  const hasTemplates = !!data?.templates?.length;

  const [selectedTemplate, setSelectedTemplate] =
    useState<CalendarTemplate | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { date: activeCalendarDate } = useWeekDate();
  const activeCalendarWeek = getISOWeek(activeCalendarDate);
  const activeCalendarYear = getYear(activeCalendarDate);
  const today = new Date();
  const currentWeek = getISOWeek(today);
  const currentYear = getYear(today);
  const isChosenWeekInThePast =
    activeCalendarYear < currentYear ||
    (activeCalendarYear === currentYear && activeCalendarWeek < currentWeek);

  return (
    <>
      <ConfirmApplyCalendarTemplateModal
        selectedTemplate={selectedTemplate}
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
      />
      <TemplateTitleContainer>
        <Typography variant="body-small-bold">
          {t("CALENDAR_WEEK_TEMPLATES_TEMPLATES_SECTION_TITLE")}
        </Typography>
        <TemplateIcon />
      </TemplateTitleContainer>
      {isLoading && (
        <Typography variant="body-small" sx={{ color: "DARK_GREY" }}>
          {t("LOADING")}
        </Typography>
      )}
      {isChosenWeekInThePast ? (
        <InThePast />
      ) : hasTemplates ? (
        data.templates.map((template: CalendarTemplate) => (
          <TemplateItemBg
            key={template.id}
            data-testid={template.name}
            onClick={() => {
              setSelectedTemplate(template);
              setIsConfirmModalOpen(true);
            }}
          >
            <TemplateItem>{template.name}</TemplateItem>
          </TemplateItemBg>
        ))
      ) : (
        <NoSavedTemplates />
      )}
    </>
  );
};

const NoSavedTemplates = () => {
  const { t } = useTranslation();
  return (
    <TemplateItemBg>
      <TemplateItem>
        <Typography variant="body-small" sx={{ color: "DARK_GREY" }}>
          {t("CALENDAR_WEEK_TEMPLATES_ADD_NEW_TEMPLATES_SECTION_NO_TEMPLATES")}
        </Typography>
      </TemplateItem>
    </TemplateItemBg>
  );
};

const InThePast = () => {
  const { t } = useTranslation();
  return (
    <TemplateItemBg style={{ pointerEvents: "none" }}>
      <TemplateItem>
        <Typography variant="body-small" sx={{ color: "DARK_GREY" }}>
          {t("CALENDER_WEEK_TEMPLATES_CHOSEN_WEEK_IN_THE_PAST")}
        </Typography>
      </TemplateItem>
    </TemplateItemBg>
  );
};

const TemplateIcon = styled(Icon.Template)`
  width: 1rem;
`;

const TemplateTitleContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin: 16px 0px 0px 16px;
`;
